import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/chart";
import { CalibrationPlotRow } from "@/http/api-types";
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";

const chartConfig = {
  // Line metrics (Y-axis)
  AvgEstimatedIpm: {
    label: "Predicted Install Rate (IPM)",
    color: "hsl(var(--chart-1))",
  },
  ApIpm: {
    label: "Actual Install Rate (IPM)",
    color: "hsl(var(--chart-2))",
  },
  AvgEstimatedWinRate: {
    label: "Predicted Win Rate",
    color: "hsl(var(--chart-3))",
  },
  WinRate: {
    label: "Actual Win Rate",
    color: "hsl(var(--chart-4))",
  },
  ImpPerBids: {
    label: "Impressions per Bid",
    color: "hsl(var(--chart-5))",
  },
  AvgBidPriceCpmMicroEur: {
    label: "Avg Bid Price (EUR CPM)",
    color: "hsl(var(--chart-1))",
  },
  PredictedCpiEur: {
    label: "Predicted CPI (EUR)",
    color: "hsl(var(--chart-2))",
  },
  ActualCpiEur: {
    label: "Actual CPI (EUR)",
    color: "hsl(var(--chart-3))",
  },

  // Bar metrics (summed values)
  RevenueEur: {
    label: "Revenue (EUR)",
    color: "hsl(var(--chart-1))",
  },
  TotalBids: {
    label: "Total Bids",
    color: "hsl(var(--chart-2))",
  },
  TotalImpressions: {
    label: "Total Impressions",
    color: "hsl(var(--chart-3))",
  },
  TotalApConversions: {
    label: "Total AP Conversions",
    color: "hsl(var(--chart-4))",
  },
} satisfies ChartConfig;

type CalibrationPlotsChartProps = {
  data: CalibrationPlotRow[];
  visibleLines: {
    AvgEstimatedIpm: boolean;
    ApIpm: boolean;
    AvgEstimatedWinRate: boolean;
    WinRate: boolean;
    ImpPerBids: boolean;
    AvgBidPriceCpmMicroEur: boolean;
    PredictedCpiEur: boolean;
    ActualCpiEur: boolean;
  };
  visibleBar: keyof typeof chartConfig;
};

function CalibrationPlotsChart({ data, visibleLines, visibleBar }: CalibrationPlotsChartProps) {
  // Sort data by VolumeQuantileBucket for proper X-axis ordering
  const sortedData = [...data].sort((a, b) => {
    const getLowerBound = (bucket: string) => {
      const match = bucket.match(/^(\d+)/);
      return match ? Number(match[1]) : 0;
    };
    return getLowerBound(a.VolumeQuantileBucket) - getLowerBound(b.VolumeQuantileBucket);
  });

  // Calculate Y-axis domain for better scaling
  const lineMetrics = sortedData
    .flatMap(d => [
      visibleLines.AvgEstimatedIpm ? d.AvgEstimatedIpm : null,
      visibleLines.ApIpm ? d.ApIpm : null,
      visibleLines.AvgEstimatedWinRate ? d.AvgEstimatedWinRate : null,
      visibleLines.WinRate ? d.WinRate : null,
      visibleLines.ImpPerBids ? d.ImpPerBids : null,
      visibleLines.AvgBidPriceCpmMicroEur ? d.AvgBidPriceCpmMicroEur / 1e6 : null, // Scale down from micro EUR to EUR
      visibleLines.PredictedCpiEur ? d.PredictedCpiEur : null,
      visibleLines.ActualCpiEur ? d.ActualCpiEur : null,
    ])
    .filter(v => v !== null);

  const minLineValue = Math.min(...lineMetrics);
  const maxLineValue = Math.max(...lineMetrics);

  return (
    <div className="space-y-4">
      {/* Chart */}
      <ChartContainer config={chartConfig} className="h-[500px]">
        <ComposedChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />

          {/* X-axis: Volume Quantile Buckets */}
          <XAxis
            dataKey="VolumeQuantileBucket"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            angle={-45}
            textAnchor="end"
            height={80}
          />

          {/* Primary Y-axis for line metrics */}
          <YAxis
            yAxisId="lines"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={[minLineValue * 0.9, maxLineValue * 1.1]}
            tickFormatter={(value: number) => {
              return `${(value * 100).toFixed(2)}%`;
            }}
          />

          {/* Secondary Y-axis for bar metrics */}
          <YAxis
            yAxisId="bars"
            orientation="right"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value: number) => {
              if (visibleBar === "RevenueEur") return `€${(value / 1000).toFixed(0)}k`;
              return value.toLocaleString();
            }}
          />

          <ChartTooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={
              <ChartTooltipContent
                formatter={(value, name) => {
                  if (name === "AvgEstimatedIpm")
                    return [`${Number(value).toFixed(2)}`, "Predicted Install Rate (IPM)"];
                  if (name === "ApIpm") return [`${Number(value).toFixed(2)}`, "Actual Install Rate (IPM)"];
                  if (name === "AvgEstimatedWinRate")
                    return [`${(Number(value) * 100).toFixed(1)}%`, "Predicted Win Rate"];
                  if (name === "WinRate") return [`${(Number(value) * 100).toFixed(1)}%`, "Actual Win Rate"];
                  if (name === "ImpPerBids") return [`${(Number(value) * 100).toFixed(1)}%`, "Impressions per Bid"];
                  if (name === "AvgBidPriceCpmMicroEur")
                    return [`${(Number(value) / 1e6).toFixed(3)} EUR`, "Avg. Bid Price CPM"];
                  if (name === "PredictedCpiEur") return [`€${Number(value).toFixed(2)}`, "Predicted CPI"];
                  if (name === "ActualCpiEur") return [`€${Number(value).toFixed(2)}`, "Actual CPI"];
                  if (name === "RevenueEur") return [`€${Number(value).toLocaleString()}`, "Revenue"];
                  if (name === "TotalBids") return [Number(value).toLocaleString(), "Total Bids"];
                  if (name === "TotalImpressions") return [Number(value).toLocaleString(), "Total Impressions"];
                  if (name === "TotalApConversions") return [Number(value).toLocaleString(), "Total AP Conversions"];
                  return [value, name];
                }}
              />
            }
          />

          {/* Bar for selected summed metric */}
          <Bar
            yAxisId="bars"
            dataKey={visibleBar}
            fill={`var(--color-${visibleBar})`}
            fillOpacity={0.3}
            stroke={`var(--color-${visibleBar})`}
            strokeWidth={1}
          />

          {/* Line for Predicted Install Rate */}
          {visibleLines.AvgEstimatedIpm && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="AvgEstimatedIpm"
              stroke="var(--color-AvgEstimatedIpm)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          {/* Line for Actual Install Rate */}
          {visibleLines.ApIpm && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="ApIpm"
              stroke="var(--color-ApIpm)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          {/* Line for Predicted Win Rate (scaled) */}
          {visibleLines.AvgEstimatedWinRate && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="AvgEstimatedWinRate"
              stroke="var(--color-AvgEstimatedWinRate)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          {/* Line for Actual Win Rate */}
          {visibleLines.WinRate && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="WinRate"
              stroke="var(--color-WinRate)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          {/* Line for Impressions per Bid */}
          {visibleLines.ImpPerBids && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="ImpPerBids"
              stroke="var(--color-ImpPerBids)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          {/* Line for Avg Bid Price (scaled down) */}
          {visibleLines.AvgBidPriceCpmMicroEur && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="AvgBidPriceCpmMicroEur"
              stroke="var(--color-AvgBidPriceCpmMicroEur)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          {/* Line for Predicted CPI */}
          {visibleLines.PredictedCpiEur && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="PredictedCpiEur"
              stroke="var(--color-PredictedCpiEur)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          {/* Line for Actual CPI */}
          {visibleLines.ActualCpiEur && (
            <Line
              yAxisId="lines"
              type="monotone"
              dataKey="ActualCpiEur"
              stroke="var(--color-ActualCpiEur)"
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          )}

          <ChartLegend content={<ChartLegendContent />} />
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}

export default CalibrationPlotsChart;
